/**
 * How each party voted, as one bar per party.
 *
 * The denominator is members who actually voted. Absences sit outside the
 * bar and are named next to it rather than folded in: counting them as
 * opposition turns a unanimous bill that half the chamber missed into a 50%
 * party, and counting them as support does the reverse.
 *
 * A party where every member was absent gets "no one voted", not 0%. Zero
 * of zero is unknown, and the API sends null for exactly this reason.
 */

import type { PartyTally } from "../api";

const NAMES: Record<string, string> = {
  D: "Democrats",
  R: "Republicans",
  I: "Independents",
};

const COLOURS: Record<string, string> = {
  D: "bg-dem",
  R: "bg-gop",
  I: "bg-ind",
};

/** Largest delegations first, so the two that decide it lead. */
function ordered(parties: Record<string, PartyTally>) {
  return Object.entries(parties).sort((a, b) => b[1].voted - a[1].voted);
}

export function VoteBar({ parties }: { parties: Record<string, PartyTally> }) {
  return (
    <ul className="space-y-3">
      {ordered(parties).map(([party, tally]) => {
        const name = NAMES[party] ?? party;
        const colour = COLOURS[party] ?? "bg-faint";
        const forIt = tally.share_yea === null ? 0 : tally.share_yea * 100;

        return (
          <li key={party}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
              <span className="font-medium">{name}</span>
              <span className="tnum text-muted">
                {tally.share_yea === null ? (
                  "no one voted"
                ) : (
                  <>
                    <strong className="font-semibold text-ink">{tally.yea}</strong> for,{" "}
                    <strong className="font-semibold text-ink">{tally.nay}</strong> against
                  </>
                )}
              </span>
            </div>

            {/* The track is everyone in the party who voted, so it is full
                whichever way they went: solid for, tinted against. An empty
                track therefore means nobody voted at all, which is a
                different fact from a party voting no to a member -- and the
                two look identical if only the yes share is drawn. */}
            <div
              className="flex h-2.5 overflow-hidden rounded-full bg-sunk"
              role="img"
              aria-label={
                tally.share_yea === null
                  ? `${name}: nobody voted`
                  : `${name}: ${tally.yea} for, ${tally.nay} against` +
                    (tally.not_voting ? `, ${tally.not_voting} absent` : "")
              }
            >
              <div className={`h-full ${colour}`} style={{ width: `${forIt}%` }} />
              <div
                className={`h-full ${colour} opacity-25`}
                style={{ width: `${tally.voted ? (tally.nay / tally.voted) * 100 : 0}%` }}
              />
            </div>

            {tally.not_voting > 0 && (
              <p className="mt-1 text-xs text-faint">
                {tally.not_voting} did not vote, and are not counted either way.
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The same split as a single thin strip, for a card where a full breakdown
 * would not fit. Segments are proportional to votes cast, so the width is
 * the size of the delegation and the shade is which way it went.
 */
export function VoteStrip({ parties }: { parties: Record<string, PartyTally> }) {
  const voted = Object.values(parties).reduce((sum, p) => sum + p.voted, 0);
  if (!voted) return null;

  return (
    <div className="flex h-1.5 overflow-hidden rounded-full bg-sunk" aria-hidden="true">
      {ordered(parties).map(([party, tally]) => {
        const colour = COLOURS[party] ?? "bg-faint";
        return (
          <div key={party} className="flex h-full" style={{ width: `${(tally.voted / voted) * 100}%` }}>
            <div className={`h-full ${colour}`} style={{ width: `${(tally.yea / tally.voted) * 100}%` }} />
            <div className={`h-full ${colour} opacity-20`} style={{ flex: 1 }} />
          </div>
        );
      })}
    </div>
  );
}
